// InputManager: wraps raw browser events and emits named actions.
//
// Downstream code subscribes to actions (`jump`, `pause`, `restart`, `look`)
// without caring whether they came from keyboard, mouse, or — eventually —
// touch / gamepad. Phase 6 will add touch + key remapping; both fit cleanly
// into this shape without changing any of the action subscribers.

const KEY_BINDINGS = {
	Space:     'jump',
	ArrowUp:   'jump',
	Escape:    'pause',
	KeyP:      'pause',
	KeyR:      'restart',
	KeyL:      'cycleLevel', // DEBUG: step through the level list (Phase 5.1)
};

const ACTIONS = ['jump', 'pause', 'restart', 'look', 'cycleLevel'];

export class InputManager {
	constructor(canvas) {
		this.canvas = canvas;
		this._listeners = Object.fromEntries(ACTIONS.map((a) => [a, []]));
		this._dragging = false;

		// Click-and-drag look. Mouse down on the canvas engages pointer lock
		// (best-effort — hides the cursor and gives raw movement deltas with
		// no screen-edge limit); mouse up releases. `look` events fire only
		// while dragging. Listeners for move/up are on document so dragging
		// off the canvas (or off-window briefly) doesn't strand the state.
		canvas.addEventListener('mousedown', (e) => {
			if (e.button !== 0) return; // left button only
			this._dragging = true;
			canvas.requestPointerLock?.();
		});

		document.addEventListener('mouseup', (e) => {
			if (e.button !== 0 || !this._dragging) return;
			this._dragging = false;
			document.exitPointerLock?.();
		});

		document.addEventListener('mousemove', (e) => {
			if (!this._dragging) return;
			this._emit('look', e.movementX, e.movementY);
		});

		window.addEventListener('keydown', (e) => {
			if (e.repeat) return;
			const action = KEY_BINDINGS[e.code];
			if (!action) return;
			// Don't preventDefault for Escape — the browser uses it for pointer
			// lock release and other things; we just observe.
			if (e.code !== 'Escape') e.preventDefault();
			this._emit(action);
		});
	}

	on(action, callback) {
		if (!this._listeners[action]) {
			throw new Error(`InputManager: unknown action "${action}"`);
		}
		this._listeners[action].push(callback);
		return this;
	}

	_emit(action, ...args) {
		for (const cb of this._listeners[action]) cb(...args);
	}
}
